/**
 * cartLineSteps.js
 * ----------------------------------------------------------------------------
 * ONE way plus, minus and remove change a line in the carts the cart hub
 * lists: the in-house cart, each shop's cart and each brand's cart. The cart
 * hub (pages/CartHub.jsx) and the quantity control on a /shop grid card
 * (components/shop/ProductCard.jsx) both dispatch these, so a line stepped on
 * the grid behaves exactly as it does in the cart.
 *
 * `kind` is the cart hub's group kind: "inhouse", "retailer" (every shop,
 * retail or wholesale) or "brand". `cartKey` is the vendor the cart is keyed
 * by (unused for the in-house cart). Each returns ONE action. Pure.
 * ----------------------------------------------------------------------------
 */
import { incrementCartItem, decrementCartItem, removeItemFromCart } from "../redux/cartRedux";
import {
  addToCart as shopAddToCart,
  removeFromCart as shopRemoveFromCart,
} from "../redux/retailerWholesalerCartSlice";
import {
  addToCart as brandAddToCart,
  removeFromCart as brandRemoveFromCart,
} from "../redux/brandCartSlice";
import { lineIdOf } from "./variantCore";

export const CART_KINDS = Object.freeze({ INHOUSE: "inhouse", RETAILER: "retailer", BRAND: "brand" });

/** A line's quantity, whichever field its cart keeps it in. */
export const cartLineQuantity = (line) => Number(line?.qty ?? line?.quantity ?? 1);

export const cartLineRemoveAction = (kind, cartKey, line) => {
  if (kind === CART_KINDS.INHOUSE) return removeItemFromCart({ _id: line._id });
  if (kind === CART_KINDS.RETAILER) return shopRemoveFromCart({ vendorId: cartKey, productId: line._id });
  return brandRemoveFromCart({ brandId: cartKey, productId: lineIdOf(line) });
};

export const cartLineIncreaseAction = (kind, cartKey, line) => {
  if (kind === CART_KINDS.INHOUSE) return incrementCartItem({ item: line });
  if (kind === CART_KINDS.RETAILER) return shopAddToCart({ vendorId: cartKey, product: line, quantity: 1 });
  return brandAddToCart({ brandId: cartKey, product: line, quantity: 1 });
};

/**
 * Minus. A step that would take the line below `minQty` (the product's
 * minimum order, 1 when it has none) takes the line out instead, the rule the
 * brand product page applies (brandQtyStep).
 */
export const cartLineDecreaseAction = (kind, cartKey, line, { minQty = 1 } = {}) => {
  if (kind === CART_KINDS.INHOUSE) return decrementCartItem({ _id: line._id });
  const floor = Math.max(1, Number(minQty) || 1);
  if (cartLineQuantity(line) - 1 < floor) return cartLineRemoveAction(kind, cartKey, line);
  if (kind === CART_KINDS.RETAILER) return shopAddToCart({ vendorId: cartKey, product: line, quantity: -1 });
  return brandAddToCart({ brandId: cartKey, product: line, quantity: -1 });
};
